<script setup>
import { ref, watch, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useDebounceFn } from '@vueuse/core';
import ContactsAPI from 'dashboard/api/contacts';

const props = defineProps({
  selectedContact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['select']);

const store = useStore();
const { t } = useI18n();

const searchQuery = ref('');
const searchResults = ref([]);
const isSearching = ref(false);
const showDropdown = ref(false);
const inputRef = ref(null);

const displayValue = computed(() => {
  if (props.selectedContact) {
    return props.selectedContact.name || props.selectedContact.email || props.selectedContact.phone_number;
  }
  return '';
});

const searchContacts = useDebounceFn(async query => {
  if (!query || query.length < 2) {
    searchResults.value = [];
    return;
  }

  isSearching.value = true;
  try {
    const response = await ContactsAPI.search(query);
    searchResults.value = response.data.payload || [];
  } catch (error) {
    searchResults.value = [];
  } finally {
    isSearching.value = false;
  }
}, 300);

watch(searchQuery, newQuery => {
  if (newQuery) {
    searchContacts(newQuery);
    showDropdown.value = true;
  } else {
    searchResults.value = [];
    showDropdown.value = false;
  }
});

const handleSelect = contact => {
  emit('select', contact);
  searchQuery.value = '';
  showDropdown.value = false;
};

const handleClear = () => {
  emit('select', null);
  searchQuery.value = '';
  showDropdown.value = false;
};

const handleFocus = () => {
  if (searchQuery.value && searchResults.value.length > 0) {
    showDropdown.value = true;
  }
};

const handleBlur = () => {
  // Delay to allow click on dropdown item
  setTimeout(() => {
    showDropdown.value = false;
  }, 200);
};

const formatContactInfo = contact => {
  const parts = [];
  if (contact.email) parts.push(contact.email);
  if (contact.phone_number) parts.push(contact.phone_number);
  return parts.join(' • ');
};

// Track broken thumbnails so we can hide the <img> and show the fallback
const brokenThumbnails = ref(new Set());

const onThumbnailError = contactId => {
  brokenThumbnails.value.add(contactId);
};

const isThumbnailValid = contact => {
  return contact.thumbnail && !brokenThumbnails.value.has(contact.id);
};
</script>

<template>
  <div class="relative">
    <!-- Selected Contact Display -->
    <div
      v-if="selectedContact"
      class="flex items-center justify-between rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2"
    >
      <div class="flex items-center gap-2.5">
        <img
          v-if="isThumbnailValid(selectedContact)"
          :src="selectedContact.thumbnail"
          :alt="selectedContact.name"
          class="size-7 rounded-full object-cover ring-1 ring-n-weak"
          @error="onThumbnailError(selectedContact.id)"
        />
        <div
          v-else
          class="flex size-7 items-center justify-center rounded-full bg-n-blue-9 text-xs font-medium text-white"
        >
          {{ selectedContact.name?.charAt(0)?.toUpperCase() || '?' }}
        </div>
        <div class="min-w-0 flex-1">
          <div class="truncate text-sm font-medium text-n-slate-12">
            {{ selectedContact.name || t('CALENDAR.FORM.UNKNOWN_CONTACT') }}
          </div>
          <div v-if="formatContactInfo(selectedContact)" class="truncate text-xs text-n-slate-10">
            {{ formatContactInfo(selectedContact) }}
          </div>
        </div>
      </div>
      <button
        type="button"
        class="ml-2 rounded p-1.5 text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12"
        @click="handleClear"
      >
        <div class="i-lucide-x size-4" />
      </button>
    </div>

    <!-- Search Input -->
    <div v-else class="relative">
      <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
        <span
          class="size-4 text-n-slate-10"
          :class="[isSearching ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-search']"
        />
      </div>
      <input
        ref="inputRef"
        v-model="searchQuery"
        type="text"
        class="w-full rounded-lg border border-n-weak bg-n-alpha-1 py-2 pl-10 pr-3 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:border-n-blue-9 focus:outline-none focus:ring-1 focus:ring-n-blue-9"
        :placeholder="t('CALENDAR.FORM.SEARCH_CONTACT')"
        @focus="handleFocus"
        @blur="handleBlur"
      />
    </div>

    <!-- Dropdown Results -->
    <div
      v-if="showDropdown && searchQuery.length >= 2"
      class="absolute z-50 mt-1 w-full overflow-hidden rounded-lg border border-n-weak bg-n-background shadow-xl"
    >
      <div
        v-if="isSearching"
        class="flex items-center justify-center py-6"
      >
        <div class="i-lucide-loader-2 size-5 animate-spin text-n-slate-10" />
      </div>

      <template v-else>
        <div v-if="searchResults.length > 0" class="max-h-64 overflow-auto">
          <button
            v-for="contact in searchResults"
            :key="contact.id"
            type="button"
            class="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-n-alpha-2"
            @click="handleSelect(contact)"
          >
            <img
              v-if="isThumbnailValid(contact)"
              :src="contact.thumbnail"
              :alt="contact.name"
              class="size-9 rounded-full object-cover ring-1 ring-n-weak"
              @error="onThumbnailError(contact.id)"
            />
            <div
              v-else
              class="flex size-9 items-center justify-center rounded-full bg-n-blue-9 text-sm font-medium text-white"
            >
              {{ contact.name?.charAt(0)?.toUpperCase() || '?' }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="truncate text-sm font-medium text-n-slate-12">
                {{ contact.name || t('CALENDAR.FORM.UNKNOWN_CONTACT') }}
              </div>
              <div v-if="formatContactInfo(contact)" class="truncate text-xs text-n-slate-10">
                {{ formatContactInfo(contact) }}
              </div>
            </div>
          </button>
        </div>

        <div
          v-else
          class="px-4 py-6 text-center text-sm text-n-slate-10"
        >
          {{ t('CALENDAR.FORM.NO_CONTACTS_FOUND') }}
        </div>
      </template>
    </div>
  </div>
</template>
